// @ts-nocheck
import {
  addTicketNote,
  approveTicket,
  completeTicket,
  createBulkTickets,
  createTicket,
  getTicketById,
  getTickets,
  getTicketUploads,
  rejectTicket,
  updateTicket,
} from '@/api/tickets';
import { useAuth } from '@/providers/AuthProvider';
import { APIErrorResponse, CreateTicketAPIResponse } from '@/types';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useTicketFilters } from '../useTicketFilters';
import { handleAPIError } from '@/utils/errorHandler';
import { CreateTicketDto, UpdateTicketDto } from '@packages/database/dtos';
import { SystemLayerKey, TicketDetail } from '@packages/database/types';
import { useMapStore } from '@/stores/mapStore';

export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;
  const layers = useMapStore((s) => s.layers);
  const ticketLayerId = layers.find((l) => l.key === SystemLayerKey.TICKET)?.id;

  const mutation = useMutation<
    TicketDetail,
    AxiosError<APIErrorResponse>,
    CreateTicketDto
  >({
    mutationFn: createTicket,
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', orgId] });
      // Refresh tickets layer
      queryClient.invalidateQueries({
        queryKey: ['features', organization?.id, ticketLayerId],
      });

      // Invalidate projects query to update ticket counts in project cards
      queryClient.invalidateQueries({
        queryKey: ['projects', orgId, ticket.projectId],
      });

      queryClient.invalidateQueries({
        queryKey: ['tickets', orgId],
      });
    },
    onError: (res: AxiosError<APIErrorResponse>, variables) => {
      if (res.response?.data.error.code === 'Auto-Dispatch') {
        toast.warning('Ticket created but unassigned.', {
          description: 'Auto-dispatch failed. Please assign a crew manually.',
        });
        queryClient.invalidateQueries({
          queryKey: ['tickets', organization?.id],
        });
        // Refresh tickets layer
        queryClient.invalidateQueries({
          queryKey: ['features', organization?.id, ticketLayerId],
        });

        queryClient.invalidateQueries({
          queryKey: ['projects', orgId, variables.projectId],
        });
      } else {
        handleAPIError(res);
      }
    },
  });

  return {
    createTicket: mutation.mutate,
    createTicketAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useTickets = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;
  const { filters } = useTicketFilters();

  const query = useQuery({
    queryKey: ['tickets', orgId, filters],
    queryFn: () => getTickets(filters),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    hasNext: query.data?.hasNext ?? false,
    hasPrev: query.data?.hasPrev ?? false,
    page: filters.page,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
};

export const useUniqueTicket = (ticketId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['ticket', orgId, ticketId],
    queryFn: () => getTicketById(ticketId!),
    enabled: !!ticketId && !!organization?.id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useUpdateTicket = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation<
    TicketDetail,
    AxiosError<APIErrorResponse>,
    UpdateTicketDto
  >({
    mutationFn: updateTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tickets', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['ticket', orgId],
      });
      queryClient.invalidateQueries({ queryKey: ['crews', orgId] });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    updateTicket: mutation.mutate,
    updateTicketAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useAddTicketNote = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation<TicketDetail, AxiosError<APIErrorResponse>, any>(
    {
      mutationFn: addTicketNote,
      onSuccess: (ticket) => {
        queryClient.invalidateQueries({
          queryKey: ['tickets', orgId],
        });
        queryClient.invalidateQueries({
          queryKey: ['ticket', orgId, ticket.id],
        });
        queryClient.invalidateQueries({ queryKey: ['crews', orgId] });
      },
      onError: (error) => {
        handleAPIError(error);
      },
    },
  );

  return {
    addTicketNote: mutation.mutate,
    addTicketNoteAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useCompleteTicket = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation<
    TicketDetail,
    AxiosError<APIErrorResponse>,
    CompleteTicketDto
  >({
    mutationFn: completeTicket,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['ticket', orgId, vars.ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ['tickets', orgId] });
    },
    onError: (error) => handleAPIError(error),
  });

  return {
    completeTicket: mutation.mutate,
    completeTicketAsync: mutation.mutateAsync,
    isCompleting: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export const useCreateBulkTickets = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const layers = useMapStore((s) => s.layers);
  const ticketLayerId = layers.find((l) => l.key === SystemLayerKey.TICKET)?.id;

  const createBulkTicketsMutation = useMutation({
    mutationFn: (ticketData: any) => createBulkTickets(ticketData),
    onSuccess: (ticket) => {
      // TODO: this should return ticket

      queryClient.invalidateQueries({
        queryKey: ['tickets', organization?.id],
      });
      // Refresh tickets layer
      queryClient.invalidateQueries({
        queryKey: ['features', organization?.id, ticketLayerId],
      });

      // Invalidate projects query to update ticket counts in project cards
      queryClient.invalidateQueries({
        queryKey: ['projects', organization?.id, ticket?.projectId],
      });
    },
    onError: (res: AxiosError<APIErrorResponse>) => {
      if (res.response?.data.error.code === 'Auto-Dispatch') {
        toast.warning('Ticket created but unassigned.', {
          description: 'Auto-dispatch failed. Please assign a crew manually.',
        });
        queryClient.invalidateQueries({
          queryKey: ['tickets', organization?.id],
        });
        queryClient.invalidateQueries({
          queryKey: ['ticketsLayer', organization?.id],
        });

        queryClient.invalidateQueries({
          queryKey: ['projects', orgId, variables.projectId],
        });
      } else {
        handleAPIError(res);
      }
    },
  });

  return {
    createBulkTickets: createBulkTicketsMutation.mutate,
    isCreating: createBulkTicketsMutation.isPending,
    error: createBulkTicketsMutation.error,
  };
};

// Photos
export const useTicketUploads = (ticketId: string) => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['tickets', ticketId, 'uploads', orgId],
    queryFn: () => getTicketUploads(ticketId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  return {
    ticketUploads: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useApproveTicket = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (ticketId: string) => approveTicket(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({
        queryKey: ['tickets', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['ticket', orgId, ticketId],
      });
    },
    onError: (error) => handleAPIError(error),
  });

  return {
    approveTicket: mutation.mutate,
    approveTicketAsync: mutation.mutateAsync,
    isApproving: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export const useRejectTicket = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: ({
      ticketId,
      rejectionReason,
    }: {
      ticketId: string;
      rejectionReason: string;
    }) => rejectTicket(ticketId, rejectionReason),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({
        queryKey: ['tickets', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['ticket', orgId],
      });
    },
    onError: (error) => handleAPIError(error),
  });
  return {
    rejectTicket: mutation.mutate,
    rejectTicketAsync: mutation.mutateAsync,
    isRejecting: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};
